import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {AccessTokenClaims, PermissionScopes, ScopeContextToGroupId, ScopeGroupContext, User} from '../../types/auth.d';
import {ID} from '../../types/global.d';

export function defaultPermissions() {
    return false
}

class PermissionNode {
    constructor(private permissions: PermissionScopes) {}

    hasMatchStr(perm: string): boolean {
        const parts = perm.split('/');
        return this.hasMatch(parts);
    }

    hasMatch(perms: string[]): boolean {
        if (this.permissions['PermissionNext']) {
            const permissionNode = this.permissions['PermissionNext']
            const perm = perms[0]; // Get the first part
            if (perm) {
                const valuePermissionNode = permissionNode[perm];
                if (valuePermissionNode) {
                    if (new PermissionNode(valuePermissionNode).hasMatch(perms.slice(1))) {
                        return true;
                    }
                }
                const wildcardPermissionNode = permissionNode['*'];
                if (wildcardPermissionNode) {
                    if (wildcardPermissionNode['Permission']) {
                        return wildcardPermissionNode['Permission'];
                    }
                    if (wildcardPermissionNode['PermissionNext']) {
                        return new PermissionNode(wildcardPermissionNode).hasMatch(perms.slice(1));
                    }
                }
            }
            return false;
        } else if (this.permissions['Permission']) {
            return this.permissions['Permission'] && perms.length === 0;
        }
        return false;
    }
}

export interface SessionState {
    user: User | null;
    accessToken: string | null;
    session: Session | null;
}

export interface Session {
    expiresOnUTC: number
    userId: ID
    groupId: ID
    permissionChecker: PermissionNode
    scopeGroupContext: ScopeGroupContext
    hasPermission(scope: string, permission: string): boolean
    hasContextPermission(context: string, permission: string): boolean
    hasAnyContextPermission(context: string[], permission: string): boolean
}

export const DEFAULT_SESSION: Session = {
    hasPermission: defaultPermissions,
    hasContextPermission: defaultPermissions,
    hasAnyContextPermission: defaultPermissions,
}

const initialState: SessionState = {
    user: null,
    accessToken: null,
    session: null,
}

const sessionSlice = createSlice({
    initialState,
    name: 'session',
    reducers: {
        logout: () => initialState,
        setUser: (state: SessionState, action: PayloadAction<User>) => {
            state.user = action.payload
        },
        setAccessToken: (state: SessionState, action: PayloadAction<string>) => {

            const accessToken = action.payload
            const parsedToken = parseJwt(accessToken)

            if (parsedToken) {

                console.log('PARSED TOKEN: ', parsedToken)

                state.accessToken = accessToken
                state.session = createSession(parsedToken)
            } else {
                state.user = null
                state.accessToken = null
                state.session = initialState.session
            }
        },
    },
});

const createSession = (parsedToken): Session => {

    const hasPermission = (scopeGroup: string, permission: string) => {
        const selectedScopeGroup = scopeGroup || '';

        if (selectedScopeGroup === '') {
            let permissions = parsedToken.permissions['*']
            if (permissions) {
                const result = new PermissionNode(permissions).hasMatchStr(permission)
                if (result) {
                    return result
                }
            }

            for (let tokenScopeGroup in parsedToken.permissions) {
                let permissions = parsedToken.permissions[tokenScopeGroup]
                if (permissions) {
                    const result = new PermissionNode(permissions).hasMatchStr(permission)
                    if (result) {
                        return result
                    }
                }
            }
        } else {
            let permissions = parsedToken.permissions['*']
            if (permissions) {
                const result = new PermissionNode(permissions).hasMatchStr(permission)
                if (result) {
                    return result
                }
            }

            permissions = parsedToken.permissions[selectedScopeGroup]
            if (permissions) {
                const result = new PermissionNode(permissions).hasMatchStr(permission)
                if (result) {
                    return result
                }
            }
        }
        return false
    }

    const hasContextPermission = (context: string, permission: string) => {
        const groups = parsedToken.scopeGroupContext ? parsedToken.scopeGroupContext.scopeContexts[context] : ['']
        if (groups) {
            for (let group in groups) {
                if (hasPermission(groups[group], permission)) {
                    return true;
                }
            }
        }
        return false;
    }

    const hasAnyContextPermission = (contexts: string[], permission: string) => {
        for (let context in contexts) {
            if (hasContextPermission(context, permission)) {
                return true
            }
        }
        return false;
    }

    return {
        expiresOnUTC: parsedToken.exp,
        userId: parsedToken.sub,
        groupId: parsedToken.memberGroup,
        permissions: parsedToken.permissions,
        scopeGroupContext: parsedToken.scopeGroupContext,
        hasPermission,
        hasContextPermission,
        hasAnyContextPermission,
    }
}

const parseJwt = (token: string | null): AccessTokenClaims | null => {
    if (token) {
        const base64Url = token.split('.')[1]
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(function (c) {
                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                })
                .join('')
        )

        return JSON.parse(jsonPayload)
    } else {
        return null;
    }
}

export const {logout, setUser, setAccessToken} = sessionSlice.actions;

export default sessionSlice;


